import {
  Button,
  Dialog,
  PlainButton,
  TextInput,
} from "@jugl-web/ui-components";
import { useTranslations, useToast } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useState, useRef } from "react";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { ReactComponent as OptIcon } from "./assets/otp.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import imgUrl from "./assets/otp-image.png";

const CONFIRMATION_VALUE = "DISABLE";

const DisableConfirmationContent = ({
  value,
  setValue,
}: {
  value: string;
  setValue: (value: string) => void;
}) => {
  const { t } = useTranslations();
  const { entity } = useEntitySelectedProvider();

  return (
    <>
      <h2 className="text-2xl">
        {t({
          id: "workspace-page.workspace-otp-disable-dialog-title",
          defaultMessage: "Confirm disabling OTP",
        })}
      </h2>
      <p className="text-dark-700 mt-2 text-sm leading-3">
        {t({
          id: "workspace-page.workspace-otp-disable-dialog-description-1",
          defaultMessage:
            "You are about to disable OTP for Guest URL shared from Workspace",
        })}{" "}
        <span className="text-primary-800 font-medium">{entity.name}</span>.
        {t({
          id: "workspace-page.workspace-otp-disable-dialog-description-2",
          defaultMessage:
            " Please pay attention that all external users will be able to enter shared Task without additional OTP authentication.",
        })}
      </p>
      <div className="mt-12 space-y-2">
        <span className="text-dark-700 flex items-center gap-1 pl-2.5 text-sm">
          {t(
            {
              id: "workspace-page.workspace-otp-disable-dialog-input-label",
              defaultMessage: "Type “{value}” to confirm an action",
            },
            {
              value: CONFIRMATION_VALUE,
            }
          )}
        </span>
        <TextInput
          placeholder={t({
            id: "common.enter",
            defaultMessage: "Enter",
          })}
          value={value}
          onChange={(e) => setValue(e.target.value.toUpperCase())}
        />
      </div>
    </>
  );
};

const EnableConfirmationContent = () => {
  const { entity } = useEntitySelectedProvider();
  const { t } = useTranslations();
  return (
    <>
      <h2 className="text-2xl">
        {t({
          id: "workspace-page.workspace-otp-enable-dialog-title",
          defaultMessage: "Confirm enabling OTP",
        })}
      </h2>
      <p className="text-dark-700 mt-2 text-sm leading-3">
        {t({
          id: "workspace-page.workspace-otp-enable-dialog-description-1",
          defaultMessage:
            "You are about to enable OTP for Guest URL shared from Workspace",
        })}{" "}
        <span className="text-primary-800 pr-1 font-medium">{entity.name}</span>
        {t({
          id: "workspace-page.workspace-otp-enable-dialog-description-2",
          defaultMessage:
            "Please pay attention that all external users would need to go through additional authentication by entering OTP, when trying to open shared Task URL.",
        })}
      </p>
    </>
  );
};

export const WorkspaceOtpDisableDialog = ({
  isOpen,
  onRequestClose,
  isEnabled = true,
}: {
  isOpen: boolean;
  onRequestClose: () => void;
  isEnabled?: boolean;
}) => {
  const { t } = useTranslations();
  const [value, setValue] = useState("");
  const { entitiesApi } = useRestApiProvider();
  const { entity } = useEntitySelectedProvider();
  const isProcessingRef = useRef(false);

  const { toast } = useToast();
  const [saveSettings, { isLoading }] =
    entitiesApi.useSaveEntitySettingsMutation();
  const isDisabled =
    value !== CONFIRMATION_VALUE || isLoading || isProcessingRef.current;

  const handleClose = () => {
    onRequestClose();
    setValue("");
  };

  const handleToggleOtp = async (enable: boolean) => {
    const response = await saveSettings({
      entityId: entity.id,
      data: {
        task: {
          customer_auth: enable ? "enabled" : "disabled",
        },
      },
    });
    handleClose();
    if ("data" in response) {
      toast(
        enable
          ? t({
              id: "workspace-page.workspace-otp-enable-success-toast",
              defaultMessage: "OTP for Guest URL was enabled",
            })
          : t({
              id: "workspace-page.workspace-otp-disable-success-toast",
              defaultMessage: "OTP for Guest URL was disabled",
            }),
        { variant: "success" }
      );
    }
    isProcessingRef.current = false;
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      className="font-secondary w-[1300px] max-w-[80vw] overflow-y-auto rounded-xl"
    >
      <header className=" rounded-t-3xl bg-white">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="font-secondary flex items-center gap-2 text-lg font-semibold text-[#383838]">
            <OptIcon />
            {t({
              id: "workspace-page.workspace-otp-title",
              defaultMessage: "OTP for Guest URL",
            })}
          </div>
          <PlainButton onClick={handleClose}>
            <CloseIcon />
          </PlainButton>
        </div>
        <div className="bg-dark-100 h-px w-full" />
      </header>

      <div className="grid grid-cols-2 gap-10 py-8 px-10">
        <img src={imgUrl} alt="otp" className="w-full" />

        <div className="py-6">
          {isEnabled ? (
            <DisableConfirmationContent value={value} setValue={setValue} />
          ) : (
            <EnableConfirmationContent />
          )}
        </div>
      </div>

      <div className="flex justify-end border-x-0 border-t border-b-0 border-solid border-[#EEF2F5] px-10 py-4">
        {isEnabled ? (
          <Button
            color="tertiary"
            size="small"
            isDisabled={isDisabled}
            onClick={() => {
              isProcessingRef.current = true;
              handleToggleOtp(false);
            }}
          >
            {t({
              id: "workspace-page.workspace-otp-disable-dialog-button",
              defaultMessage: "Disable OTP",
            })}
          </Button>
        ) : (
          <Button
            color="primary"
            size="small"
            onClick={() => {
              isProcessingRef.current = true;
              handleToggleOtp(true);
            }}
            isDisabled={isProcessingRef.current || isLoading}
          >
            {t({
              id: "workspace-page.workspace-otp-enable-dialog-button",
              defaultMessage: "Enable OTP",
            })}
          </Button>
        )}
      </div>
    </Dialog>
  );
};
